import { supabase, type Database } from '../lib/supabase'
import type { Driver, CreateDriverRequest, UpdateDriverRequest } from '../types/driver'
import type { SearchFilters } from '../types/search'
import { validatePhoneNumber, normalizePhoneNumber } from '../utils/validation'
import { apiCall } from '../utils/networkErrorHandler'
import { logServiceError } from '../utils/errorLogger'

type DriverRow = Database['public']['Tables']['drivers']['Row']
type DriverInsert = Database['public']['Tables']['drivers']['Insert']
type DriverUpdate = Database['public']['Tables']['drivers']['Update']

export class DriverService {
  /**
   * Convert database row to Driver interface
   */
  private mapRowToDriver(row: DriverRow): Driver {
    return {
      id: row.id,
      fullName: row.full_name,
      phoneNumber: row.phone_number,
      status: row.status,
      lineUserId: row.line_user_id || undefined,
      avatarImage: row.avatar_url || undefined,
      color: row.avatar_color || '#3B82F6',
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at)
    }
  }

  /**
   * Convert Driver interface to database insert format
   */
  private mapDriverToInsert(driver: CreateDriverRequest): DriverInsert {
    return {
      full_name: driver.fullName,
      phone_number: driver.phoneNumber,
      status: driver.status || 'available',
      line_user_id: driver.lineUserId || null,
      avatar_url: driver.avatarImage || null,
      avatar_color: driver.color || '#3B82F6'
    }
  }

  /**
   * Convert Driver interface to database update format
   */
  private mapDriverToUpdate(driver: UpdateDriverRequest): DriverUpdate {
    const update: DriverUpdate = {}

    if (driver.fullName !== undefined) {
      update.full_name = driver.fullName
    }
    if (driver.phoneNumber !== undefined) {
      update.phone_number = driver.phoneNumber
    }
    if (driver.status !== undefined) {
      update.status = driver.status
    }
    if (driver.lineUserId !== undefined) {
      update.line_user_id = driver.lineUserId
    }
    if (driver.avatarImage !== undefined) {
      update.avatar_url = driver.avatarImage
    }
    if (driver.color !== undefined) {
      update.avatar_color = driver.color
    }

    return update
  }

  /**
   * Create a new driver
   * Requirements: 2.1 - Store driver with all required fields
   */
  async createDriver(driverData: CreateDriverRequest): Promise<Driver> {
    return apiCall(async () => {
      // Validate required fields
      if (!driverData || typeof driverData !== 'object') {
        throw new Error('Invalid driver data provided')
      }

      if (!driverData.fullName || typeof driverData.fullName !== 'string' || !driverData.fullName.trim()) {
        throw new Error('Full name is required')
      }

      if (!driverData.phoneNumber || typeof driverData.phoneNumber !== 'string' || !driverData.phoneNumber.trim()) {
        throw new Error('Phone number is required')
      }

      // Validate phone number format
      if (!validatePhoneNumber(driverData.phoneNumber)) {
        throw new Error('Phone number format is invalid. Please use format like: +66812345678 or 0812345678')
      }

      // Normalize phone number before saving to database
      const normalizedData = {
        ...driverData,
        phoneNumber: normalizePhoneNumber(driverData.phoneNumber)
      }

      const insertData = this.mapDriverToInsert(normalizedData)

      const { data, error } = await supabase
        .from('drivers')
        .insert(insertData)
        .select()
        .single()

      if (error) {
        const serviceError = new Error(`Failed to create driver: ${error.message}`)
        logServiceError(serviceError, 'DriverService', 'createDriver', driverData)
        throw serviceError
      }

      return this.mapRowToDriver(data)
    }, 'createDriver')
  }

  /**
   * Get driver by ID
   */
  async getDriverById(id: string): Promise<Driver | null> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('id', id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Driver not found
        }
        const serviceError = new Error(`Failed to get driver: ${error.message}`)
        logServiceError(serviceError, 'DriverService', 'getDriverById', { id })
        throw serviceError
      }

      return this.mapRowToDriver(data)
    }, 'getDriverById')
  }

  /**
   * Get all drivers
   */
  async getAllDrivers(): Promise<Driver[]> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        const serviceError = new Error(`Failed to get drivers: ${error.message}`)
        logServiceError(serviceError, 'DriverService', 'getAllDrivers')
        throw serviceError
      }

      return data.map((row: DriverRow) => this.mapRowToDriver(row))
    }, 'getAllDrivers')
  }

  /**
   * Update driver information
   * Requirements: 2.2 - Modify existing record and preserve data consistency
   */
  async updateDriver(id: string, driverData: UpdateDriverRequest): Promise<Driver> {
    // Validate fields if provided
    if (driverData.fullName !== undefined && !driverData.fullName.trim()) {
      throw new Error('Full name cannot be empty')
    }
    if (driverData.phoneNumber !== undefined) {
      if (!driverData.phoneNumber.trim()) {
        throw new Error('Phone number cannot be empty')
      }
      if (!validatePhoneNumber(driverData.phoneNumber)) {
        throw new Error('Phone number format is invalid. Please use format like: +66812345678 or 0812345678')
      }
      // Normalize phone number before updating
      driverData = {
        ...driverData,
        phoneNumber: normalizePhoneNumber(driverData.phoneNumber)
      }
    }

    const updateData = this.mapDriverToUpdate(driverData)

    const { data, error } = await supabase
      .from('drivers')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Driver not found')
      }
      throw new Error(`Failed to update driver: ${error.message}`)
    }

    return this.mapRowToDriver(data)
  }

  /**
   * Delete driver
   * Requirements: 2.3 - Remove driver record and handle schedule dependencies
   */
  async deleteDriver(id: string): Promise<void> {
    // Check if driver exists in schedule entries
    const { data: scheduleEntries, error: scheduleError } = await supabase
      .from('schedule_entries')
      .select('id')
      .eq('driver_id', id)
      .limit(1)

    if (scheduleError) {
      throw new Error(`Failed to check driver dependencies: ${scheduleError.message}`)
    }

    if (scheduleEntries && scheduleEntries.length > 0) {
      throw new Error('Cannot delete driver: they have existing schedule entries')
    }

    const { error } = await supabase
      .from('drivers')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete driver: ${error.message}`)
    }
  }

  /**
   * Search and filter drivers
   * Requirements: 2.4 - Support driver search and filtering
   */
  async searchDrivers(filters: SearchFilters): Promise<Driver[]> {
    let query = supabase
      .from('drivers')
      .select('*')

    // Apply driver name filter (partial match)
    if (filters.driverName?.trim()) {
      query = query.ilike('full_name', `%${filters.driverName.trim()}%`)
    }

    // Apply driver ID filter (exact match)
    if (filters.driverId?.trim()) {
      query = query.eq('id', filters.driverId.trim())
    }

    query = query.order('created_at', { ascending: false })

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to search drivers: ${error.message}`)
    }

    return data.map((row: DriverRow) => this.mapRowToDriver(row))
  }

  /**
   * Get drivers by status
   */
  async getDriversByStatus(status: 'available' | 'unavailable'): Promise<Driver[]> {
    const { data, error } = await supabase
      .from('drivers')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(`Failed to get drivers by status: ${error.message}`)
    }

    return data.map((row: DriverRow) => this.mapRowToDriver(row))
  }

  /**
   * Get available drivers (convenience method)
   */
  async getAvailableDrivers(): Promise<Driver[]> {
    return this.getDriversByStatus('available')
  }

  /**
   * Update driver availability status
   * Requirements: 2.5 - Maintain driver status
   */
  async updateDriverStatus(id: string, status: 'available' | 'unavailable'): Promise<Driver> {
    return this.updateDriver(id, { status })
  }

  /**
   * Validate phone number format
   * Requirements: 2.4 - Validate phone numbers follow proper format requirements
   */
  validatePhoneNumber(phoneNumber: string): boolean {
    return validatePhoneNumber(phoneNumber)
  }
}

// Export singleton instance
export const driverService = new DriverService()